import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

import ChannelIcon from "../ChannelIcon";
import { i18n } from "../../translate/i18n";

const UNREAD_GREEN = "#15803D";

const useStyles = makeStyles((theme) => {
  const dark = theme.mode === "dark" || theme.palette.type === "dark";
  return {
    row: {
      display: "flex",
      alignItems: "stretch",
      gap: 12,
      padding: "0 14px",
      "&:hover $chev, &:focus-within $chev": { opacity: 1 },
      "@media (hover: none)": { "& $chev": { opacity: 1 } },
    },
    selected: {
      backgroundColor: dark ? "rgba(255,255,255,0.08) !important" : "#EEF1F6 !important",
    },
    avatarWrap: { position: "relative", flexShrink: 0, alignSelf: "center", padding: "10px 0" },
    channel: {
      position: "absolute",
      right: -3,
      bottom: 8,
      width: 18,
      height: 18,
      borderRadius: 9,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
      border: `2px solid ${theme.palette.background.paper}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    main: {
      flexGrow: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 3,
      padding: "10px 0",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    line: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    name: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 15,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    time: { flexShrink: 0, fontSize: 12, fontWeight: 500 },
    preview: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 13.5,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "& p, & div": { display: "inline", margin: 0 },
    },
    presence: { color: UNREAD_GREEN, fontStyle: "italic" },
    sibling: { flexShrink: 0, fontSize: 16, color: "#B45309" },
    badge: {
      flexShrink: 0,
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      boxSizing: "border-box",
      borderRadius: 10,
      backgroundColor: UNREAD_GREEN,
      color: "#FFFFFF",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    accept: {
      flexShrink: 0,
      height: 26,
      padding: "0 12px",
      border: "none",
      borderRadius: 13,
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      "&:disabled": { opacity: 0.6, cursor: "default" },
    },
    chev: {
      flexShrink: 0,
      opacity: 0,
      transition: "opacity .12s",
      width: 26,
      height: 24,
      borderRadius: 6,
      padding: 0,
      backgroundColor: dark ? "rgba(255,255,255,0.12)" : "#E5E7EB",
    },
  };
});

// Fila de ticket del modo simple (doc 0446): solo presentación; la lógica vive en TicketListItemCustom.
const SimpleRow = ({
  selected,
  onOpen,
  avatar,
  provider,
  name,
  timeText,
  preview,
  presenceText,
  unread,
  isPending,
  hasSiblings,
  loading,
  onAccept,
  onOpenActions,
  acceptLabel,
}) => {
  const classes = useStyles();
  const hasUnread = unread > 0;

  return (
    <ListItem
      button
      dense
      selected={selected}
      onClick={onOpen}
      className={classes.row}
      classes={{ selected: classes.selected }}
    >
      <span className={classes.avatarWrap}>
        {avatar}
        <span className={classes.channel}>
          <ChannelIcon provider={provider} size={16} />
        </span>
      </span>
      <span className={classes.main}>
        <span className={classes.line}>
          <span className={classes.name} style={{ fontWeight: hasUnread ? 700 : 600 }}>
            {name}
          </span>
          {hasSiblings && (
            <Tooltip title={i18n.t("tickets.simple.otherConversation")}>
              <WarningRoundedIcon className={classes.sibling} aria-label={i18n.t("tickets.simple.otherConversation")} />
            </Tooltip>
          )}
          <span className={classes.time} style={{ color: hasUnread && !isPending ? UNREAD_GREEN : undefined }}>
            {timeText}
          </span>
        </span>
        <span className={classes.line}>
          <span className={classes.preview}>
            {presenceText ? <span className={classes.presence}>{presenceText}</span> : preview}
          </span>
          {hasUnread && !isPending && (
            <span className={classes.badge}>{unread > 99 ? "99+" : unread}</span>
          )}
          {isPending && (
            <button
              type="button"
              className={classes.accept}
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                onAccept();
              }}
            >
              {acceptLabel}
            </button>
          )}
          {onOpenActions && (
            <IconButton
              className={classes.chev}
              aria-label={i18n.t("tickets.simple.chatActions")}
              onClick={(e) => {
                e.stopPropagation();
                onOpenActions(e);
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </span>
      </span>
    </ListItem>
  );
};

export default SimpleRow;
